import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import logo from '../Logo.svg';
import {
  LayoutDashboard,
  Users,
  FileText,
  LogOut,
  Menu,
  X,
  BarChart3,
  Search,
  ShieldCheck,
  Package,
  Microscope,
  Settings,
  Bell,
  CheckCheck,
  Building2,
  MapPin,
  CreditCard,
  ClipboardList,
  Tag,
  FlaskConical,
} from 'lucide-react';
import authService from '../services/authService';
import notificationService from '../services/notificationService';

export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const user = authService.getCurrentUser();
    setUserData(user);
  }, []);

  // Load unread count on mount and every 60s
  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUnreadCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(typeof count === 'number' ? count : (count?.count ?? 0));
    } catch {
      // Fail silently
    }
  };

  const loadNotifications = async () => {
    try {
      const data = await notificationService.getUnreadNotifications();
      setNotifications(Array.isArray(data) ? data.slice(0, 10) : []);
    } catch {
      setNotifications([]);
    }
  };

  const toggleNotifications = async () => {
    const newOpen = !isNotifOpen;
    setIsNotifOpen(newOpen);
    if (newOpen) {
      await loadNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setNotifications([]);
      setIsNotifOpen(false);
    } catch {
      // Fail silently
    }
  };

  const handleMarkRead = async (notif) => {
    try {
      await notificationService.markAsRead(notif.id);
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Fail silently
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'All Operators', href: '/search-operators', icon: Search },
    { name: 'My Operators', href: '/operators', icon: Users },
    { name: 'License Management', href: '/licenses', icon: ShieldCheck },
    { name: 'Products', href: '/products', icon: Package },
    { name: 'Inventory', href: '/inventory', icon: Package },
    { name: 'Lab & COA', href: '/lab-coa', icon: Microscope },
    { name: 'Compliance Reports', href: '/compliance-reports', icon: FileText },
    { name: 'Analytics', href: '/analytics', icon: BarChart3 },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const adminNavigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Organizations', href: '/admin/organizations', icon: Building2 },
    { name: 'Operators (Admin)', href: '/admin/operators', icon: MapPin },
    { name: 'Products', href: '/admin/products', icon: Package },
    { name: 'Product Categories', href: '/admin/product-categories', icon: Tag },
    { name: 'Payments', href: '/admin/payments', icon: CreditCard },
    { name: 'Plans', href: '/admin/subscription-plans', icon: Package },
    { name: 'Audit Logs', href: '/admin/audit-logs', icon: ClipboardList },
    { name: 'Trial Requests', href: '/admin/trial-requests', icon: FlaskConical },
  ];

  const isAdmin = userData?.roleName === 'Admin';

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        to={item.href}
        onClick={onClick}
        className={`group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition ${
          isActive
            ? 'bg-primary text-primary-foreground shadow-md'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <item.icon className="mr-3 h-5 w-5" />
        {item.name}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow border-r border-border bg-card pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-4 mb-6">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-xl font-display font-bold text-foreground">GreenInteliX</span>
            </Link>
          </div>
          <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
            {isAdmin
              ? adminNavigation.map((item) => <NavLink key={item.name} item={item} />)
              : navigation.map((item) => <NavLink key={item.name} item={item} />)
            }
          </nav>
          <div className="px-4 mt-auto">
            <Button variant="outline" className="w-full" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
          <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-card border-r border-border animate-slide-in">
            <div className="flex items-center justify-between px-4 pt-5 pb-4">
              <Link to="/" className="flex items-center space-x-2">
                <img src={logo} alt="Verdionix" className="h-9 w-auto" style={{ mixBlendMode: 'multiply' }} />
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="h-6 w-6" />
              </Button>
            </div>
            <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
              {isAdmin
                ? adminNavigation.map((item) => <NavLink key={item.name} item={item} onClick={() => setSidebarOpen(false)} />)
                : navigation.map((item) => <NavLink key={item.name} item={item} onClick={() => setSidebarOpen(false)} />)
              }
            </nav>
            <div className="px-4 mb-4">
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 flex-shrink-0 border-b border-border bg-background/80 backdrop-blur-md">
          <button
            type="button"
            className="px-4 border-r border-border text-muted-foreground focus:outline-none lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <h2 className="text-lg font-semibold text-foreground">
                {[...navigation, ...adminNavigation].find((item) => item.href === location.pathname)?.name}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              {/* Notification Bell */}
              <div className="relative" ref={notifRef}>
                <button
                  type="button"
                  onClick={toggleNotifications}
                  className="relative p-2 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  aria-label="Notifications"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {isNotifOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-card border border-border rounded-xl shadow-xl z-50">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                      {notifications.length > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          <CheckCheck className="h-3 w-3" />
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                          No unread notifications
                        </div>
                      ) : (
                        notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border last:border-0"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{notif.title ?? notif.message}</p>
                              {notif.message && notif.title && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                              )}
                              {notif.createdAt && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {new Date(notif.createdAt).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleMarkRead(notif)}
                              className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                              title="Mark as read"
                            >
                              <CheckCheck className="h-4 w-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-sm text-muted-foreground">
                {userData?.email || 'User'}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
};
